import React, { useEffect, useMemo, useState } from 'react'
import { Transition } from '@headlessui/react'
import classNames from 'classnames'
import { When } from 'react-if'
import { Image } from '@/atoms/Image'
import { ResponsiveBackgroundImage } from '@/atoms/ResponsiveBackgroundImage'
import { AsFigCaption, AsH2, AsH3, CaptionMD, CaptionSM, EyebrowLG, HeadingXS, TitleMD } from '@/atoms/Text'
import { formatDimensions, formatTitleLogo } from '@/services/ContentCatalog'
import { ContentfulCloudinaryImageType } from '@/services/EmailCapturePageService/EmailCapturePageService'
import { buildCloudinaryImagePathFromContentfulObject, getCloudinaryImageUrl } from '@/utils/Cloudinary'
import { useTranslate } from '@/utils/translate/translate-client'
import { GuildEmailInput } from '@/views/GuildJoinView/GuildEmailInput'
import { useGuildSignupContext } from '../GuildSignupContext'

interface GuildTestimonialSectionProps {
  hasUser: boolean
  landing?: boolean
}

export const GuildTestimonialSection: React.FC<GuildTestimonialSectionProps> = ({ hasUser, landing = false }) => {
  const { t } = useTranslate('guild')
  const { guildTestimonialSection, catalogTitle, showSocialProof } = useGuildSignupContext()
  const [currentSlide, setCurrentSlide] = useState(0)
  const titleLogo = catalogTitle ? formatTitleLogo(catalogTitle) : null
  const {
    desktopBackgroundImageUrl,
    tabletBackgroundImageUrl,
    mobileBackgroundImageUrl,
    testimonialsCollection,
    audience,
    cinemaScore,
    tomatometer,
    guildSpecific,
  } = guildTestimonialSection ?? {}
  useEffect(() => {
    const interval = setInterval(() => {
      if (currentSlide === (testimonialsCollection?.items?.length ?? 0) - 1) {
        setCurrentSlide(0)
      } else {
        setCurrentSlide(currentSlide + 1)
      }
    }, 6500)
    return () => clearInterval(interval)
  }, [currentSlide, testimonialsCollection?.items?.length])

  const mobileBackgroundUrl = useMemo(() => {
    if (!guildSpecific) {
      return mobileBackgroundImageUrl
    } else {
      return 'https://images.angelstudios.com/image/upload/v1762959433/angel-studios/backgrounds/Mobile-bkgd.webp'
    }
  }, [guildSpecific, mobileBackgroundImageUrl])

  const tabletBackgroundUrl = useMemo(() => {
    if (!guildSpecific) {
      return tabletBackgroundImageUrl
    } else {
      return 'https://images.angelstudios.com/image/upload/v1762959433/angel-studios/backgrounds/Tablet-bkgd.webp'
    }
  }, [guildSpecific, tabletBackgroundImageUrl])

  const desktopBackgroundUrl = useMemo(() => {
    if (!guildSpecific) {
      return desktopBackgroundImageUrl
    } else {
      return 'https://images.angelstudios.com/image/upload/v1762959433/angel-studios/backgrounds/Desktop-bkgd.webp'
    }
  }, [guildSpecific, desktopBackgroundImageUrl])

  if (!guildTestimonialSection && !showSocialProof) {
    return null
  }

  return (
    <section
      aria-labelledby="guild-testimonial-title"
      id="guild-testimonial"
      className={classNames(
        'relative flex w-full flex-col items-center text-pretty px-4 py-16 text-center text-white md:items-start md:px-2 md:py-32 lg:px-20',
        { 'pt-[328px] pb-[67px] lg:pt-[146px] lg:pb-[203px] md:px-8': guildSpecific },
      )}
    >
      {desktopBackgroundUrl && (
        <ResponsiveBackgroundImage
          src={desktopBackgroundUrl}
          className={classNames('md:bg-top-right bg-cover bg-bottom bg-no-repeat', {
            'bg-top': guildSpecific,
          })}
          preBackgroundStyles="linear-gradient(180deg, rgba(21, 21, 21, 0.3) 60%, #151515 100%), "
          srcUrls={{
            xs: mobileBackgroundUrl ?? desktopBackgroundUrl,
            sm: mobileBackgroundUrl ?? desktopBackgroundUrl,
            md: tabletBackgroundUrl ?? desktopBackgroundUrl,
            lg: desktopBackgroundUrl,
            xl: desktopBackgroundUrl,
            '2xl': desktopBackgroundUrl,
          }}
        />
      )}
      <div
        className={classNames(
          'relative flex w-full flex-col items-center gap-8 text-center text-white md:w-auto md:min-w-[570px]',
          {
            'md:items-start': guildSpecific,
          },
        )}
      >
        <div
          className={classNames('flex max-w-[107px] flex-col items-center gap-6 md:max-w-[130px] md:p-0', {
            'md:items-start': guildSpecific,
          })}
        >
          {titleLogo && (
            <Image
              alt={'Guild Logo'}
              loading="lazy"
              className="min-w-[178px]"
              src={getCloudinaryImageUrl({
                path: titleLogo.cloudinaryPath,
                transforms: 'e_trim',
                ...formatDimensions([500, 'auto'], titleLogo),
              })}
              {...formatDimensions([500, 'auto'], titleLogo)}
            />
          )}
          {guildSpecific && (
            <Image
              alt={'Guild Logo'}
              loading="lazy"
              className=""
              width={90}
              height={112}
              src={getCloudinaryImageUrl({
                path: '/v1704922101/angel-app/guild/angel-guild-logo-full.webp',
                transforms: 'e_trim',
              })}
            />
          )}
          {!guildSpecific && (
            <EyebrowLG weight="semibold" className="uppercase" id="guild-testimonial-title">
              {t('reviews', 'Reviews')}
            </EyebrowLG>
          )}
        </div>
        {landing && (
          <HeadingXS
            as={AsH2}
            weight="bold"
            id="guild-predicts-winners-title"
            className="md:photon-heading-sm lg:photon-heading-md"
            color="white"
          >
            {t('trustedFamiliesEverywhere', 'Trusted By Families Everywhere')}
          </HeadingXS>
        )}
        <Transition
          show
          appear
          enter="transition-opacity duration-1000"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="transition-opacity duration-5500"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <Testimonial
            quote={testimonialsCollection?.items?.[currentSlide]?.quote ?? ''}
            author={testimonialsCollection?.items?.[currentSlide]?.author ?? ''}
            guildSpecific={Boolean(guildSpecific)}
          />
        </Transition>
        <ul className="flex h-full min-w-[98px] flex-row gap-1 md:items-end">
          {testimonialsCollection?.items?.map((item, index) => {
            const itemKey = `${index}${item}`
            const key = `${item}${index}`

            return (
              <li
                key={itemKey}
                onClick={() => setCurrentSlide(index)}
                className="flex cursor-pointer items-center justify-center p-1"
              >
                <Transition
                  show={currentSlide === index}
                  className="max-h-[10px] overflow-hidden rounded-full"
                  enter="transition ease-in-out duration-850"
                  enterFrom="-translate-x-4 scale-x-0 w-2.5 bg-core-gray-500"
                  enterTo="translate-x-0 scale-x-1 w-8 bg-white"
                >
                  <div
                    className={classNames('bg-white max-h-2.5 h-2.5 w-2.5 rounded-full ', {
                      '!w-8': currentSlide === index,
                    })}
                    key={key}
                  />
                </Transition>
                {currentSlide !== index && (
                  <div
                    className={classNames('h-2.5 w-2.5 rounded-full bg-core-gray-500', {
                      'bg-white !w-8': currentSlide === index,
                    })}
                    key={key}
                  />
                )}
              </li>
            )
          })}
        </ul>
        {!guildSpecific && (
          <div className="flex flex-row gap-6 px-5 lg:gap-12">
            <When condition={Boolean(tomatometer)}>
              <Rating icon={tomatometer?.icon} rating={tomatometer?.rating} author={tomatometer?.author} />
            </When>
            <When condition={Boolean(audience)}>
              <Rating icon={audience?.icon} rating={audience?.rating} author={audience?.author} />
            </When>
            <When condition={Boolean(cinemaScore)}>
              <Rating icon={cinemaScore?.icon} rating={cinemaScore?.rating} author={cinemaScore?.author} />
            </When>
          </div>
        )}
        <div
          className={classNames('mb-0 flex min-h-[72px] w-full items-center justify-center px-4', {
            'md:px-0 md:-mx-4 lg:mx-0': guildSpecific,
          })}
        >
          <GuildEmailInput hasUser={hasUser} isHero={true} />
        </div>
      </div>
    </section>
  )
}

const Testimonial = ({
  quote,
  author,
  guildSpecific = false,
}: {
  quote?: string
  author?: string
  guildSpecific?: boolean
}) => {
  return (
    <div
      className={classNames(
        'flex min-h-[211px] flex-col justify-between gap-[17px] rounded-2xl bg-core-gray-700/30 px-6 py-10 backdrop-blur-2xl md:min-w-[326px] md:max-w-[326px] lg:min-w-[452px] lg:max-w-[452px] xl:min-w-[537px] xl:max-w-[537px] 2xl:min-w-[576px] 2xl:max-w-[576px]',
        {
          'lg:items-start min-h-[359px] lg:min-w-[576px] lg:min-h-[239px] bg-core-gray-700/10': guildSpecific,
        },
      )}
    >
      <TitleMD
        className={classNames('2xl:photon-title-lg text-pretty', {
          'lg:text-left photon-paragraph-sm 2xl:photon-paragraph-sm leading-[26px] font-[350px]': guildSpecific,
        })}
      >
        {quote}
      </TitleMD>
      <CaptionMD weight={guildSpecific ? 'bold' : 'medium'}>{author}</CaptionMD>
    </div>
  )
}

const Rating = ({
  icon,
  rating,
  author,
  size = 56,
}: {
  icon: ContentfulCloudinaryImageType | ContentfulCloudinaryImageType[]
  rating: string | undefined | null
  author: string | undefined | null
  size?: number
}) => {
  return (
    <figure className={classNames('flex flex-col items-center m-4 text-core-gray-400')}>
      <div className="mx-auto w-[56px]">
        <Image
          src={buildCloudinaryImagePathFromContentfulObject(icon, 'w_56,h_56,c_fill')}
          width={size}
          height={size}
          alt={`${author} Rating`}
          loading="lazy"
        />
      </div>
      <div className="text-center">
        <HeadingXS as={AsFigCaption} className="mb-1 mt-2">
          {rating}
        </HeadingXS>
        <CaptionSM as={AsH3}>{author}</CaptionSM>
      </div>
    </figure>
  )
}
